import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { JobStatus } from 'cli/enums';
import { cliJobService } from 'cli/services';
import { ICliJob } from 'cli/types';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';

// A details call that fails is retried this many times at the poll interval before the job is
// reported as lost: one dropped request must not end the caller's in-progress state.
export const MAX_FAILED_CHECK_RETRIES = 3;

interface IJobPoll {
  abortController: AbortController;
  failedChecks: number;
  finishedCallback: (cliJob: ICliJob) => void;
  intervalMilliseconds: number;
  isSettled: boolean;
  notificationPosition: NotificationPosition;
  onError?: (error?: unknown) => void;
  onProgress?: (cliJob: ICliJob) => void;
  stoppedCallback: (cliJob: ICliJob) => void;
}

export const useJobChecker = () => {
  const DEFAULT_INTERVAL_MILLISECONDS = 2000;

  const { t: translate } = useTranslation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const t = useMemo(() => translate, []);

  const { showNotification } = useNotificationContext();

  const onCheckJobStatus = useCallback(
    async (cliJob: ICliJob, poll: IJobPoll) => {
      // The catch below also sees callback exceptions; a job whose callback ran is not a lost poll.
      const onPollFailed = (error?: unknown) => {
        if (poll.isSettled) {
          return;
        }

        poll.isSettled = true;
        poll.onError?.(error);
      };

      try {
        const { record } = await cliJobService.getDetails(
          { job: cliJob.id },
          poll.abortController.signal
        );

        if (!record) {
          showNotification({
            title: t('titleError'),
            message: t('errorInvalidCliJob'),
            type: ApiStatus.ERROR,
            position: poll.notificationPosition,
          });
          onPollFailed();

          return;
        }

        poll.failedChecks = 0;

        const isCompleted = onCheckSuccess(record, poll);

        if (!isCompleted) {
          try {
            poll.onProgress?.(record);
          } catch {
            // A progress display must never end the poll or report it as lost.
          }

          setTimeout(() => {
            onCheckJobStatus(cliJob, poll);
          }, poll.intervalMilliseconds);
        }

        return;
      } catch (error) {
        if (poll.isSettled) {
          return;
        }

        if (poll.failedChecks < MAX_FAILED_CHECK_RETRIES) {
          poll.failedChecks += 1;

          setTimeout(() => {
            onCheckJobStatus(cliJob, poll);
          }, poll.intervalMilliseconds);

          return;
        }

        poll.abortController.abort();
        onPollFailed(error);
      }
    },
    [showNotification, t]
  );

  const checkJobStatus = useCallback(
    (
      cliJob: ICliJob,
      finishedHandler: (cliJob: ICliJob) => void,
      stopppedHandler: (cliJob: ICliJob) => void,
      options: {
        intervalMilliseconds?: number;
        notificationPosition?: NotificationPosition;
        // A poll that cannot tell how the job ended: the details call kept failing (after
        // MAX_FAILED_CHECK_RETRIES retries) or found no job.
        onError?: (error?: unknown) => void;
        // Each poll that finds the job still pending, e.g. to show progress it writes to `result`.
        onProgress?: (cliJob: ICliJob) => void;
      } = {}
    ) => {
      const {
        intervalMilliseconds = DEFAULT_INTERVAL_MILLISECONDS,
        notificationPosition = NotificationPosition.TOP,
        onError,
        onProgress,
      } = options;

      onCheckJobStatus(cliJob, {
        abortController: new AbortController(),
        failedChecks: 0,
        finishedCallback: finishedHandler,
        intervalMilliseconds,
        isSettled: false,
        notificationPosition,
        onError,
        onProgress,
        stoppedCallback: stopppedHandler,
      });
    },
    [onCheckJobStatus]
  );

  const onCheckSuccess = (cliJob: ICliJob, poll: IJobPoll) => {
    switch (cliJob.status) {
      case JobStatus.FINISHED:
        poll.abortController.abort();
        poll.isSettled = true;
        poll.finishedCallback(cliJob);

        return true;
      // Expired is terminal too — polling it forever would never release the caller's lock.
      case JobStatus.FAILED:
      case JobStatus.CANCELED:
      case JobStatus.EXPIRED:
        poll.abortController.abort();
        poll.isSettled = true;
        poll.stoppedCallback(cliJob);

        return true;
      default:
        return false;
    }
  };

  return {
    checkJobStatus,
  };
};
